import React from 'react';
import dayjs from 'dayjs';
import { formatNumberWithCommas } from '@rocco/utils/format-number';
import { useAtomValue } from 'jotai';
import { isSalesModeAtom } from '@rocco/states/apAdminUiContext';

export interface DirectoryItemKPI {
    ID: number;
    ClientType: string;
    ItemType: string;
    PeriodClientProjected12MonthEnquiries?: number | null | undefined;
    PeriodClientProjected12MonthPageViews?: number | null | undefined;
    PeriodClientProjected12MonthReferrals?: number | null | undefined;
    PeriodClientProjected12MonthSaves?: number | null | undefined;
    PeriodClientTotalEnquiries?: number | null | undefined;
    PeriodClientTotalPageViews?: number | null | undefined;
    PeriodClientTotalReferrals?: number | null | undefined;
    PeriodClientTotalSaves?: number | null | undefined;
    PeriodItemEnquiries?: number | null | undefined;
    PeriodItemPageViews?: number | null | undefined;
    PeriodItemReferrals?: number | null | undefined;
    PeriodItemSaves?: number | null | undefined;
    PeriodStart?: string | null | undefined;
    PredictedWinRate?: number | null | undefined;
    SubscriptionAmount?: number | null | undefined;
}

interface TileKPIContextMenuProps {
    kpi: DirectoryItemKPI;
}

export const TileKPIContextMenu = ({ kpi }: TileKPIContextMenuProps) => {
    const { ItemType } = kpi;

    const isSalesMode = useAtomValue(isSalesModeAtom);

    if (!isSalesMode) {
        return null;
    }

    if (ItemType === 'Professional') {
        return (
            <>
                <div className="flex cursor-default items-center justify-between gap-8">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-bold leading-normal text-gray-900">
                            Client Results since:
                        </span>
                    </div>
                    <span className="text-xs font-bold leading-normal text-gray-400">
                        {kpi.PeriodStart
                            ? dayjs(
                                  Math.max(
                                      new Date(kpi.PeriodStart).getTime(),
                                      new Date('2022-01-01').getTime(),
                                  ),
                              ).format('D MMMM YYYY')
                            : null}
                    </span>
                </div>
                <div className="flex cursor-default items-center justify-between gap-8">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-normal leading-normal text-gray-900">
                            Enquiries
                        </span>
                    </div>
                    <span className="text-xs font-normal leading-normal text-gray-400">
                        {kpi.PeriodClientTotalEnquiries
                            ? formatNumberWithCommas(
                                  kpi.PeriodClientTotalEnquiries,
                              )
                            : 0}
                    </span>
                </div>

                <div className="flex cursor-default items-center justify-between gap-8">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-normal leading-normal text-gray-900">
                            Referrals
                        </span>
                    </div>
                    <span className="text-xs font-normal leading-normal text-gray-400">
                        {kpi.PeriodClientTotalReferrals
                            ? formatNumberWithCommas(
                                  kpi.PeriodClientTotalReferrals,
                              )
                            : 0}
                    </span>
                </div>
                <div className="flex cursor-default items-center justify-between gap-8">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-normal leading-normal text-gray-900">
                            Saves
                        </span>
                    </div>
                    <span className="text-xs font-normal leading-normal text-gray-400">
                        {kpi.PeriodClientTotalSaves
                            ? formatNumberWithCommas(kpi.PeriodClientTotalSaves)
                            : 0}
                    </span>
                </div>

                <div className="flex cursor-default items-center justify-between gap-8">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-normal leading-normal text-gray-900">
                            Page Views
                        </span>
                    </div>
                    <span className="text-xs font-normal leading-normal text-gray-400">
                        {kpi.PeriodClientTotalPageViews
                            ? formatNumberWithCommas(
                                  kpi.PeriodClientTotalPageViews,
                              )
                            : null}
                    </span>
                </div>

                <div className="mt-2 flex cursor-default items-center justify-between gap-8">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-bold leading-normal text-gray-900">
                            Client 12 Month Projection
                        </span>
                    </div>
                </div>
                <div className="flex cursor-default items-center justify-between gap-8">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-normal leading-normal text-gray-900">
                            Enquiries
                        </span>
                    </div>
                    <span className="text-xs font-normal leading-normal text-gray-400">
                        {kpi.PeriodClientProjected12MonthEnquiries
                            ? formatNumberWithCommas(
                                  kpi.PeriodClientProjected12MonthEnquiries,
                              )
                            : 0}
                    </span>
                </div>

                <div className="flex cursor-default items-center justify-between gap-8">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-normal leading-normal text-gray-900">
                            Referrals
                        </span>
                    </div>
                    <span className="text-xs font-normal leading-normal text-gray-400">
                        {kpi.PeriodClientProjected12MonthReferrals
                            ? formatNumberWithCommas(
                                  kpi.PeriodClientProjected12MonthReferrals,
                              )
                            : 0}
                    </span>
                </div>
                <div className="flex cursor-default items-center justify-between gap-8">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-normal leading-normal text-gray-900">
                            Saves
                        </span>
                    </div>
                    <span className="text-xs font-normal leading-normal text-gray-400">
                        {kpi.PeriodClientProjected12MonthSaves
                            ? formatNumberWithCommas(
                                  kpi.PeriodClientProjected12MonthSaves,
                              )
                            : 0}
                    </span>
                </div>

                <div className="flex cursor-default items-center justify-between gap-8">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-normal leading-normal text-gray-900">
                            Page Views
                        </span>
                    </div>
                    <span className="text-xs font-normal leading-normal text-gray-400">
                        {kpi.PeriodClientProjected12MonthPageViews
                            ? formatNumberWithCommas(
                                  kpi.PeriodClientProjected12MonthPageViews,
                              )
                            : 0}
                    </span>
                </div>
            </>
        );
    }

    return (
        <>
            <div className="flex cursor-default items-center justify-between gap-8">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-bold leading-normal text-gray-900">
                        {ItemType} Results since:
                    </span>
                </div>
                <span className="text-xs font-bold leading-normal text-gray-400">
                    {kpi.PeriodStart
                        ? dayjs(
                              Math.max(
                                  new Date(kpi.PeriodStart).getTime(),
                                  new Date('2022-01-01').getTime(),
                              ),
                          ).format('D MMMM YYYY')
                        : null}
                </span>
            </div>
            <div className="flex cursor-default items-center justify-between gap-8">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-normal leading-normal text-gray-900">
                        Enquiries
                    </span>
                </div>
                <span className="text-xs font-normal leading-normal text-gray-400">
                    {kpi.PeriodItemEnquiries
                        ? formatNumberWithCommas(kpi.PeriodItemEnquiries)
                        : 0}
                </span>
            </div>

            <div className="flex cursor-default items-center justify-between gap-8">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-normal leading-normal text-gray-900">
                        Referrals
                    </span>
                </div>
                <span className="text-xs font-normal leading-normal text-gray-400">
                    {kpi.PeriodItemReferrals
                        ? formatNumberWithCommas(kpi.PeriodItemReferrals)
                        : 0}
                </span>
            </div>
            <div className="flex cursor-default items-center justify-between gap-8">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-normal leading-normal text-gray-900">
                        Saves
                    </span>
                </div>
                <span className="text-xs font-normal leading-normal text-gray-400">
                    {kpi.PeriodItemSaves
                        ? formatNumberWithCommas(kpi.PeriodItemSaves)
                        : 0}
                </span>
            </div>

            <div className="flex cursor-default items-center justify-between gap-8">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-normal leading-normal text-gray-900">
                        Page Views
                    </span>
                </div>
                <span className="text-xs font-normal leading-normal text-gray-400">
                    {kpi.PeriodItemPageViews
                        ? formatNumberWithCommas(kpi.PeriodItemPageViews)
                        : null}
                </span>
            </div>

            <div className="mt-2 flex cursor-default items-center justify-between gap-8">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-bold leading-normal text-gray-900">
                        Client Sitewide 12 Month Projection
                    </span>
                </div>
            </div>
            <div className="flex cursor-default items-center justify-between gap-8">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-normal leading-normal text-gray-900">
                        Enquiries
                    </span>
                </div>
                <span className="text-xs font-normal leading-normal text-gray-400">
                    {kpi.PeriodClientProjected12MonthEnquiries
                        ? formatNumberWithCommas(
                              kpi.PeriodClientProjected12MonthEnquiries,
                          )
                        : 0}
                </span>
            </div>

            <div className="flex cursor-default items-center justify-between gap-8">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-normal leading-normal text-gray-900">
                        Referrals
                    </span>
                </div>
                <span className="text-xs font-normal leading-normal text-gray-400">
                    {kpi.PeriodClientProjected12MonthReferrals
                        ? formatNumberWithCommas(
                              kpi.PeriodClientProjected12MonthReferrals,
                          )
                        : 0}
                </span>
            </div>
            <div className="flex cursor-default items-center justify-between gap-8">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-normal leading-normal text-gray-900">
                        Saves
                    </span>
                </div>
                <span className="text-xs font-normal leading-normal text-gray-400">
                    {kpi.PeriodClientProjected12MonthSaves
                        ? formatNumberWithCommas(
                              kpi.PeriodClientProjected12MonthSaves,
                          )
                        : 0}
                </span>
            </div>

            <div className="flex cursor-default items-center justify-between gap-8">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-normal leading-normal text-gray-900">
                        Page Views
                    </span>
                </div>
                <span className="text-xs font-normal leading-normal text-gray-400">
                    {kpi.PeriodClientProjected12MonthPageViews
                        ? formatNumberWithCommas(
                              kpi.PeriodClientProjected12MonthPageViews,
                          )
                        : 0}
                </span>
            </div>
        </>
    );
};
